import { useCallback, useMemo } from 'react';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import type {
  PlannerHeatView,
  SearchSelectOption,
} from '@voll-intelligence/intelligence-ui-kit/flight-planner';
import { useFlightPlannerPort } from '../flight_planner_port.js';
import { airportNaming } from '../ui/airport_naming.js';
import { useFlightAirportHistoryPort } from '../airport_history_port.js';
import {
  airportLabelOf,
  airportOptionOf,
} from '@voll-intelligence/intelligence-ui-kit/flight-planner';
import { PlannerForm, useRecentAirports } from '@voll-intelligence/intelligence-ui-kit';
import type { FlightSearchParams } from '@voll-intelligence/intelligence-ui-kit';
import '@voll-intelligence/intelligence-ui-kit/flight-explorer.css';
import { flightPlannerCopy } from '../ui/flight_planner_copy.js';

/** Meses PEDIDOS na janela de calor (o cap do provider, e o mesmo do card). São
 *  três contra os dois que o popover da faixa abre lado a lado, e a folga é de
 *  propósito: o read é UM por janela, então o 3º mês vem no mesmo request e dá um
 *  passo de pager grátis — sem ele, todo ‹ › re-ancora a janela (fetch + shimmer). */
const HEAT_MONTHS = 3;

/** Mês corrente 'YYYY-MM' por partes LOCAIS (não `toISOString`): em UTC, entre
 *  21h e 24h BRT do último dia do mês a janela pularia o mês corrente. */
function currentMonth(): string {
  const now = new Date();
  return `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** Fire-and-forget vazio: sem o seam `flightHeat` não há a quem pedir calor.
 *  Módulo e não arrow inline porque a identidade é lida por effect no form. */
const NO_HEAT_FETCH = () => {};

/**
 * **A faixa de busca do explorer é o `PlannerForm`** — a MESMA fileira
 * `QUEM · DE ⇄ PARA · QUANDO` (mais as abas das três formas de viagem) que monta
 * a busca na conversa. Não existe formulário compacto próprio aqui.
 *
 * O que existia antes era uma strip bespoke: rota como TEXTO read-only ("mudar
 * rota é outra conversa"), um `<select>` nativo de cabine e um `Buscar` próprio.
 * A suposição da rota fixa era o defeito — o `submit` re-emitia `params.origin/
 * destination`, então corrigir o pedido nesta tela nunca chegava a corrigir a
 * ROTA, que é justamente o dado que mais se erra ao pedir um voo. E, ao lado
 * disso, a tela mantinha uma segunda gramática pra mesma pergunta: dois
 * desenhos, duas cópias de rótulo, duas réguas de calendário.
 *
 * **Este componente é só o adaptador de vocabulário** (o mesmo papel que o
 * `FlightPlannerCard` faz no chat): traduz o pedido por pernas do explorer para
 * o retrato de mount do form, e o echo do form de volta para
 * `FlightSearchParams`. Zero controle próprio.
 *
 * ## A FORMA da viagem é derivada, não é campo de fio
 *
 * `FlightSearchParams` não tem "tripType": o `returnDate` é que decide (é assim
 * que o `POST /flight/search` nasce round-trip, e é assim que o reducer relê o
 * modo em `search_started`). Então a aba entra semeada do pedido — com volta é
 * ida-e-volta, sem volta é só ida, com roteiro é multi — e sai pelo echo do
 * form, que só manda `returnDate` quando a pessoa escolheu uma.
 *
 * ## Cabine e viajantes NÃO moram aqui
 *
 * **Cabine** desceu pra coluna de cortes (`FlightFilterPanel`), onde é o único
 * controle que RE-BUSCA em vez de filtrar. A faixa continua carregando a cabine
 * VIGENTE no echo — re-buscar por data ou rota não pode zerar a escolha feita na
 * coluna.
 *
 * **Viajantes** são do form (campo QUEM), mas do que ele ecoa só o
 * `policyTravelerId` tem casa no fio de busca: `FlightSearchParams` espelha o
 * `FlightSearchBodySchema` do app-integration, que não tem `travelers` nem
 * `passengers`. Escolher N pessoas aqui move o SUJEITO DA POLÍTICA e nada mais —
 * e o pedido vigente responde quando o form não ecoa ninguém (o explorer recebe
 * `policyTravelerId` sem os nomes, então o campo QUEM abre vazio).
 *
 * É por isso que o campo entra em `purpose='policy'` neste degrau (o `layout`
 * `band` do form pede): a pilha de avatares com "N viajantes" descrevia uma
 * contagem de passageiros que a busca não tem onde levar. Aqui ele mostra de quem
 * é a política — e no card segue contando viajantes, onde N pessoas são N
 * vouchers na emissão.
 *
 * ## Editar no multi é editar o ROTEIRO
 *
 * A aba multi TEM `Buscar` — na linha, como a binária —, mas `Editar trechos` e
 * `Atualizar` continuam existindo ao lado dele: o popover só APLICA o roteiro ao
 * estado da faixa (a mesma régua do "Confirmar datas" da aba binária), e é o
 * `Buscar` quem ecoa. O que sai do `Buscar` é o roteiro INTEIRO (`onEditLegs`),
 * nunca a perna em cena — reescrever uma perna sozinha produziria um pedido que
 * o resto do roteiro contradiz, e no chat o pick que saísse dele seria recusado
 * na adoção server-side, que casa rota+data da oferta com `slots.legs[legIndex]`.
 * Trocar para uma aba binária (`onSearch`) é o outro lado da mesma regra: a aba é
 * autoritativa sobre a forma, então o pedido passa a ser de uma perna só.
 *
 * ## Calor
 *
 * **A cor de preço vem do seam `flightHeat`** do `GenUiConfig` (o host é dono do
 * estado; `fetch` é fire-and-forget, como na tela de busca do booking): a grade
 * abre colorida igual à do card do chat. Seam ausente ⇒ grade neutra e clicável
 * com "sem tendência de preço" — o mesmo degrade do card com a SERP fora do ar.
 */
export function SearchBand({
  params,
  legs,
  busy,
  readOnly,
  onSearch,
  onEditLegs,
  onDirtyChange,
}: Readonly<{
  /** O pedido VIGENTE da perna em cena — o retrato que semeia a faixa. */
  params: FlightSearchParams;
  /** Roteiro INTEIRO, só no multi-trecho: com ele a faixa abre no modo multi e
   *  mostra a cadeia toda (`GRU → REC → SSA`), em vez de apenas a perna ativa —
   *  que é o que o `params` carrega. */
  legs?: { origin: string; destination: string; departDate: string }[];
  busy: boolean;
  readOnly?: boolean;
  onSearch: (params: FlightSearchParams) => void;
  /** O roteiro foi RE-EDITADO (rota, datas, nº de trechos) — o echo `search_multi`
   *  do form. Sink SEPARADO do `onSearch` porque o que sai daqui não é uma
   *  re-busca: é um pedido de forma diferente (N pernas), e quem o recebe tem de
   *  refazer o fan-out inteiro. Editar a faixa no multi é editar o ROTEIRO, nunca a
   *  perna em cena — um `Buscar` que reescrevesse UMA perna de uma viagem de N
   *  seria um botão que mente, e o pick que saísse dele seria recusado na adoção. */
  onEditLegs: (legs: { origin: string; destination: string; departDate: string }[]) => void;
  /** Repasse cru do PlannerForm: campos vivos divergem do último Buscar. */
  onDirtyChange?: (dirty: boolean) => void;
}>) {
  const { t, locale } = useGenUiI18n();
  // Os textos do form no locale da conversa: memo por locale, uma identidade só.
  const plannerCopy = useMemo(() => flightPlannerCopy(locale), [locale]);
  const naming = useMemo(() => airportNaming(locale), [locale]);
  const { flightHeat, ports } = useGenUiConfig();
  const travelers = ports?.travelers;
  const plannerPort = useFlightPlannerPort();
  // Aeroportos pelo MESMO port do card do chat (`flightPlanner`), com o MESMO
  // mapeamento Airport→opção: um combobox de rota, uma fonte. `useCallback`
  // estabiliza a referência — é dep do effect de busca do PlaceField, e uma arrow
  // inline re-dispararia a busca a cada render. Sem port (preview/storybook) a
  // lista degrada pra vazia, como no card.
  const searchAirports = useCallback(
    (term: string): Promise<SearchSelectOption[]> =>
      plannerPort
        ? plannerPort.searchAirports(term).then((as) => as.map((a) => airportOptionOf(a, naming)))
        : Promise.resolve([]),
    [plannerPort, naming],
  );
  // O histórico do viajante no DE/PARA, pelo port da conversa (o form o recebe por prop).
  const recentAirports = useRecentAirports(useFlightAirportHistoryPort(), naming);
  // Retrato do mount (o form é dono do estado a partir daí). A aba nasce da FORMA
  // derivada do pedido — ver a nota do componente.
  const initial = useMemo(
    () => ({
      route: { origin: params.origin, destination: params.destination },
      ...(legs && legs.length > 1
        ? {
            tripType: 'multi_leg' as const,
            // O wire da perna do planner chama a data de `date`, não `departDate`.
            legs: legs.map((l) => ({
              origin: l.origin,
              destination: l.destination,
              date: l.departDate,
            })),
          }
        : {
            tripType: params.returnDate ? ('round_trip' as const) : ('one_way' as const),
            departDate: params.departureDate,
            ...(params.returnDate ? { returnDate: params.returnDate } : {}),
          }),
    }),
    [params.origin, params.destination, params.departureDate, params.returnDate, legs],
  );

  // Sem seam de calor, a vista é só a moldura (rota + janela): a grade abre sem cor.
  const fallbackHeat = useMemo(
    (): PlannerHeatView => ({
      route: { origin: params.origin, destination: params.destination },
      window: { fromMonth: currentMonth(), count: HEAT_MONTHS },
    }),
    [params.origin, params.destination],
  );

  // Congela SEM desmontar: os campos ficam em cena, inertes, e o CTA sai (a mesma
  // régua do Confirmar do explorer).
  const frozen = busy || Boolean(readOnly);

  return (
    <div className="fex-band">
      <PlannerForm
        initial={initial}
        heat={flightHeat?.heat ?? fallbackHeat}
        // **O degrau do MEIO**: a fileira inteira (abas + QUEM + DE ⇄ PARA + QUANDO +
        // Buscar) e nada mais. `open` traria a grade de 3 meses INLINE — a faixa
        // passava de 300px de altura e empurrava a lista de voos para fora da tela,
        // na tela cuja função É a lista. Aqui a grade abre no popover do QUANDO, que
        // é o que o desenho manda pra bancada (`painelDeDatas`): "não tenho grade
        // embaixo, põe a sua aqui dentro".
        layout="band"
        readOnly={frozen}
        // O CTA é o "Buscar" desta tela, não o "Buscar voos nessas datas" do card:
        // aqui a lista do resultado está logo abaixo, e o rótulo longo repetiria o
        // que a própria faixa mostra.
        submitLabel={t('flightExplorer.searchCta')}
        searchAirports={searchAirports}
        recentAirports={recentAirports}
        cityOfAirport={naming.cityByAirport}
        airportLabel={(iata) => airportLabelOf(iata, naming)}
        travelerDirectory={travelers}
        copy={plannerCopy}
        locale={locale}
        // Com seam, o form pede o heat da janela que abrir (o mesmo comportamento
        // do card do chat); sem seam não há o que pedir.
        autoFetchHeat={Boolean(flightHeat)}
        heatLoading={flightHeat?.loading ?? false}
        fetchHeat={flightHeat?.fetch ?? NO_HEAT_FETCH}
        onDirtyChange={onDirtyChange}
        onSubmit={(sel) => {
          if (sel.action === 'search_multi') {
            // De volta ao vocabulário do explorer (`departDate`).
            onEditLegs(
              sel.legs.map((l) => ({
                origin: l.origin,
                destination: l.destination,
                departDate: l.date,
              })),
            );
            return;
          }
          // O que resta é `search` (o form só emite as duas ações).
          //
          // O sujeito da política é o do PEDIDO VIGENTE, nunca o que o form ecoa: o
          // campo QUEM é leitura na faixa (`planner_form.tsx`, `readOnly || band`)
          // exatamente porque re-buscar sob outro sujeito divergia em silêncio de
          // `slots.policyTravelerId` no servidor — ofertas precificadas sob um,
          // reserva atribuída a outro.
          const { policyTravelerId } = params;
          onSearch({
            origin: sel.origin,
            destination: sel.destination,
            departureDate: sel.outboundDate,
            ...(sel.returnDate ? { returnDate: sel.returnDate } : {}),
            // A cabine é da COLUNA de cortes; a faixa só a carrega adiante.
            ...(params.cabin ? { cabin: params.cabin } : {}),
            ...(policyTravelerId ? { policyTravelerId } : {}),
          });
        }}
      />
    </div>
  );
}
